import React, { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS } from "@contentful/rich-text-types";
import { ReferenciaContenido } from "../../../interfaces/ReferenciaContenido";
import { graphql, useStaticQuery } from "gatsby";

// Components //
import TitleSection from "../../molecules/title-section";
import EventCard from "../../molecules/event-card/eventCard";
import ShareModal from "../../molecules/share-modal/shareModal";
import NoContentMessage from "../../molecules/no-content-message/noContentMessage";
import PreviousArrowButton from "../../atoms/previous-arrow-button/previousArrowButton";
import NextArrowButton from "../../atoms/next-arrow-button/nextArrowButton";

import useBrowserMode from "../../../shared/hooks/useBrowserMode";

// Styles //
import "./eventsSection.scss";


const EventsSection = (props: ReferenciaContenido) => {
    const noContentEvent = useStaticQuery(graphql`
        query NOCONTENTEVENTOS {
            noContentPersonas: contentfulPlantillaContenido(contentful_id: {eq: "2dt9lXNqjZbN2k5Tkcxk6X"}) {
                tituloBanner
                descripcion {
                    raw
                }
                imagenPrincipal {
                    gatsbyImageData(
                        layout: CONSTRAINED
                        outputPixelDensities: [0.5, 1, 2]
                        breakpoints: [320, 768, 1024, 1440]
                        placeholder: BLURRED
                        sizes: "(max-width: 320px) 320px, (max-width: 768px) 768px, (max-width: 1024px) 1024px, 1440px"
                    )
                    description
                }
            }
            noContentEmpresas: contentfulPlantillaContenido(contentful_id: {eq: "BC75RgxlmLYMaMURxeNNn"}) {
                tituloBanner
                descripcion {
                    raw
                }
                imagenPrincipal {
                    gatsbyImageData(
                        layout: CONSTRAINED
                        outputPixelDensities: [0.5, 1, 2]
                        breakpoints: [320, 768, 1024, 1440]
                        placeholder: BLURRED
                        sizes: "(max-width: 320px) 320px, (max-width: 768px) 768px, (max-width: 1024px) 1024px, 1440px"
                    )
                    description
                }
            }
        }
    `);

    const eventos = useRef<HTMLDivElement>(null);
    const {
        tituloGeneral,
        descripcion,
        referenciaEventos,
        referenciaPlantillaContenido: refPlantilla,
        tipoComponente
    } = props;

    const [slider, setSlider] = useState<any>();
    const [sliderIndex, setSliderIndex] = useState(0);
    const [active, setActive] = useState(0);
    const [visibility, setVisibility] = useState(false);
    const { isBrowser } = useBrowserMode();
    const [currentUrl, setCurrentUrl] = useState("");

    const popupCloseHandler = (e) => {
        setVisibility(e);
    };
    const onSetCurrentUrl = (url: string) => {
        setCurrentUrl(url)
    }

    const RenderOptions = {
        renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => {
                return <p className="tabs-panel__text">{children}</p>;
            }
        },
    };

    const handleTabs = (e) => {
        const index = parseInt(e.target.id, 0);
        if (index !== active) {
            setActive(index);
        }
    };

    const CarouselGeneral = {
        dots: true,
        infinite: false,
        speed: 300,
        slidesToShow: 2,
        arrows: false,
        slidesToScroll: 2,
        waitForAnimate: false,
        autoplay: false,
        fade: false,
        autoplaySpeed: 10000,
        variableWidth: true,
        beforeChange: (_: number, next: number) => {
            setSliderIndex(next);
        },
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                    initialSlide: 1,
                    variableWidth: false,
                },
            },
            {
                breakpoint: 570,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    variableWidth: false,
                },
            },
        ],
    };

    const goToNext = () => {
        if (sliderIndex < slider.props.children.length - 1) {
            setSliderIndex(sliderIndex + 1);
            slider.slickNext();
        }
    };

    const goToBack = () => {
        if (sliderIndex > 0) {
            setSliderIndex(sliderIndex - 1);
            slider.slickPrev();
        }
    };

    const isActiveBack = () => sliderIndex > 0;
    const isActiveNext = () => sliderIndex < slider?.props.children.length - 1;

    useEffect(() => {
        if (eventos.current && window.location.href.includes('eventos')) {
            eventos.current.scrollIntoView({ behavior: 'smooth'});
        }
    }, []);

    const transformData = (arr) => {
        if(arr) {
            return arr.map((sub) => {
                return {
                    ...sub.referenciaInterna.referenciaCabecera.fields.referenciaEvento.fields,
                    slug: sub.slug,
                }
            })
        }
    }

    return (
        <>
            {refPlantilla && refPlantilla.length >= 1 && (
                <div className="o-event-section" ref={eventos}>
                    <div className="container">
                        <TitleSection
                            title={tituloGeneral}
                            introText={descripcion.json.content[0].content[0].value}
                        />
                        <div className="tabs">
                            {refPlantilla.map((item, i) => (
                                <button onClick={handleTabs} className={`tabs__tab ${active === i ? "active" : ""}`} id={String(i)} key={i}>
                                    {item.tituloBanner}
                                </button>
                            ))}
                        </div>
                        <>
                            {refPlantilla.map((item, i) => (
                                <div className={`tabs-panel ${active === i ? "active" : ""}`} key={i}>
                                    {item.descripcion && documentToReactComponents(item.descripcion.json, RenderOptions)}
                                    {(() => {
                                        if (item.referenciaSubcategoria == undefined || item.referenciaSubcategoria.length == 0) {
                                            return tipoComponente == "EventosPersonas" ? (
                                                <NoContentMessage 
                                                    className="evento-personas"
                                                    title={noContentEvent.noContentPersonas.tituloBanner} 
                                                    subtitle={noContentEvent.noContentPersonas.descripcion} 
                                                    imagenPrincipal={noContentEvent.noContentPersonas.imagenPrincipal} 
                                                />
                                            ) : (
                                                <NoContentMessage title={noContentEvent.noContentEmpresas.tituloBanner} subtitle={noContentEvent.noContentEmpresas.descripcion} imagenPrincipal={noContentEvent.noContentEmpresas.imagenPrincipal} className="evento-empresas"/>
                                            )
                                        } else {
                                            return (
                                                <div className={`o-event-section__container`}>
                                                    {item.referenciaSubcategoria.length > 2 && (
                                                        <div className="arrows">
                                                            <PreviousArrowButton canGoPrev={isActiveBack()} onClick={goToBack}/>
                                                            <NextArrowButton canGoNext={isActiveNext()} onClick={goToNext}/>
                                                        </div>
                                                    ) }
                                                    {active === i && <Slider
                                                        ref={(c) => {
                                                            setSlider(c);
                                                        }}
                                                        {...CarouselGeneral}
                                                    >
                                                        {transformData(item.referenciaSubcategoria).map((item, i) => (
                                                            <EventCard
                                                                key={i}
                                                                contentful_id={item.contentful_id}
                                                                imagenPrincipal={item.imagenPrincipal.fields}
                                                                tipoEvento={item.tipoEvento}
                                                                modoEvento={item.modoEvento}
                                                                tituloEvento={item.tituloEvento}
                                                                descripcion={item.textoIntroductorio}
                                                                fechaEvento={item.fechaEvento}
                                                                horaEvento={item.horaEvento}
                                                                lugarEvento={item.lugarEvento}
                                                                compartirLink={`${window.location.origin}/servicio-de-empleo/eventos/${item.slug}`}
                                                                onClickShare={popupCloseHandler}
                                                                setLinkUrl={onSetCurrentUrl}
                                                                slug={item.slug}
                                                            />
                                                        ))}
                                                    </Slider>}
                                                </div>
                                            );
                                        }
                                    })()}
                                </div>
                            ))}
                        </>
                    </div>
                    <ShareModal isOpen={visibility} onClose={popupCloseHandler} link={currentUrl} />
                </div>
            )}
            {referenciaEventos && referenciaEventos.length >= 1 && (
                <div className="o-event-section" ref={eventos}>
                    <div className="container">
                        <TitleSection
                            title={tituloGeneral}
                            introText={descripcion.json.content[0].content[0].value}
                        />
                        <div className={`o-event-section__container`}>
                            <div className="arrows-slider">
                                <span
                                    className={`arrow icon-chevron-left ${isActiveBack() ? "active" : ""
                                        }`}
                                    onClick={goToBack}
                                ></span>
                                <span
                                    className={`arrow  icon-chevron-right  ${isActiveNext() ? "active" : ""
                                        }`}
                                    onClick={goToNext}
                                ></span>
                            </div>
                            <Slider
                                ref={(c) => {
                                    setSlider(c);
                                }}
                                {...CarouselGeneral}
                            >
                                {referenciaEventos.map((item, i) => (
                                    <EventCard
                                        key={i}
                                        imagenPrincipal={item.imagenPrincipal}
                                        tipoEvento={item.tipoEvento}
                                        modoEvento={item.modoEvento}
                                        tituloEvento={item.tituloEvento}
                                        descripcion={item.descripcion}
                                        fechaEvento={item.fechaEvento}
                                        horaEvento={item.horaEvento}
                                        lugarEvento={item.lugarEvento}
                                        tituloBoton={item.tituloBoton}
                                        redireccionBoton={item.redireccionBoton}
                                        compartirLink={encodeURIComponent(`${isBrowser ? window.location.href : ''}?scroll=eventos&image=${item.imagenPrincipal.file.url}&title=${item.tituloEvento}&date=${item.fechaEvento}&hour=${item.horaEvento}`.replace(/ /g, '%20'))}
                                        onClickShare={popupCloseHandler}
                                        setLinkUrl={onSetCurrentUrl}
                                    />
                                ))}
                            </Slider>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EventsSection;
